import { useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';

import {
  KEYWORDS,
  FUNCTIONS,
  CONSTANTS,
  PROPERTIES,
  DERIVED_ACCESSORS,
  DERIVED_ACCESSOR_UNITS,
} from './constants';
import { tokenize, classifyIdentifier, tokenColorClass, extractLetBindings } from './tokenize';


// Approximate font metrics for caret positioning (12px ui-monospace).
const EDITOR_LINE_HEIGHT = 18;
const EDITOR_CHAR_WIDTH = 7.2;
const EDITOR_PADDING_TOP = 8;
const EDITOR_PADDING_LEFT = 12;

// Render an inline annotation (the gray "→ unit" suffix at the end of a
// line in the editor overlay).
function renderAnnotation(ann) {
  const muted = 'text-gray-400 italic';
  const bad = 'text-red-500 italic';
  if (ann.kind === 'error') {
    return (
      <span className={bad} style={{ marginLeft: '1.5em' }}>
        {`# ⚠ ${ann.message}`}
      </span>
    );
  }
  if (ann.kind === 'let') {
    return (
      <span className={muted} style={{ marginLeft: '1.5em' }}>
        {`# → ${ann.unit}`}
      </span>
    );
  }
  if (ann.kind === 'set_input' || ann.kind === 'set_output') {
    const arrow = ann.convertible ? '✓' : '⚠';
    const cls = ann.convertible ? muted : bad;
    // When the framework will use a species property to bridge measures
    // at the IO boundary (energy↔mass via lhv, volume↔mass via density,
    // moles↔mass via molar_mass), surface it so the reader knows what's
    // happening. No-route conversions (e.g. mmBtu → kWh) skip the label.
    const route = ann.conversion_route ? ` (${ann.conversion_route})` : '';
    return (
      <span className={cls} style={{ marginLeft: '1.5em' }}>
        {`# → ${ann.expr_unit} → ${ann.target_unit}${route} ${arrow}`}
      </span>
    );
  }
  // assert: no annotation
  return null;
}

const BalanceEditor = ({
  className = '',
  control,
  register,
  setValue,
  inputSlugs,
  outputSlugs,
  paramSlugs,
  inputUnits,
  outputUnits,
  paramUnits,
  ioProperties,
  annotations,
}) => {
  const text = useWatch({ control, name: 'balance' }) ?? '';
  const knownSlugs = useMemo(
    () => ({ inputs: inputSlugs, outputs: outputSlugs, params: paramSlugs }),
    [inputSlugs, outputSlugs, paramSlugs],
  );

  // Render tokens grouped by line so we can append an annotation per line.
  const rendered = useMemo(() => {
    const tokens = tokenize(text);
    const lines = [[]];
    for (const tok of tokens) {
      if (tok.type === 'newline') {
        lines.push([]);
      } else {
        lines[lines.length - 1].push(tok);
      }
    }

    return lines.map((lineTokens, lineIdx) => {
      const lineNo = lineIdx + 1;
      const ann = annotations?.[lineNo];
      return (
        <span key={lineIdx}>
          {lineTokens.map((tok, i) => {
            let kind = tok.type;
            if (kind === 'identifier') kind = classifyIdentifier(tok.value, knownSlugs);
            const cls = tokenColorClass(kind);
            return (
              <span key={i} className={cls}>
                {tok.value}
              </span>
            );
          })}
          {ann ? renderAnnotation(ann) : null}
          {'\n'}
        </span>
      );
    });
  }, [text, knownSlugs, annotations]);

  // Shared font/padding/sizing keeps caret alignment between layers.
  const sharedStyle = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '12px',
    lineHeight: `${EDITOR_LINE_HEIGHT}px`,
    padding: `${EDITOR_PADDING_TOP}px ${EDITOR_PADDING_LEFT}px`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
    border: 0,
    boxSizing: 'border-box',
  };

  const [scroll, setScroll] = useState({ top: 0, left: 0 });
  const textareaRef = useRef(null);

  // Merge our own ref with react-hook-form's.
  const balanceReg = register('balance');
  const setRefs = useCallback(
    el => {
      textareaRef.current = el;
      if (typeof balanceReg.ref === 'function') balanceReg.ref(el);
    },
    [balanceReg],
  );

  // --- Autocomplete ----------------------------------------------------

  const [ac, setAc] = useState({
    open: false,
    items: [],
    selectedIdx: 0,
    position: { top: 0, left: 0 },
    start: 0,
    end: 0,
  });
  const closeAc = useCallback(() => setAc(a => (a.open ? { ...a, open: false } : a)), []);

  const baseCandidates = useMemo(
    () => [
      ...inputSlugs.map((s, i) => ({
        slug: s,
        kind: 'input',
        tag: i === 0 ? 'anchor' : 'input',
        unit: inputUnits[i],
      })),
      ...outputSlugs.map((s, i) => ({
        slug: s,
        kind: 'output',
        tag: 'output',
        unit: outputUnits[i],
      })),
      ...paramSlugs.map((s, i) => ({
        slug: s,
        kind: 'param',
        tag: 'param',
        unit: paramUnits[i],
      })),
      ...Array.from(FUNCTIONS).map(s => ({ slug: s, kind: 'function', tag: 'fn' })),
      ...Array.from(CONSTANTS).map(s => ({ slug: s, kind: 'constant', tag: 'const' })),
      ...Array.from(KEYWORDS).map(s => ({ slug: s, kind: 'keyword', tag: 'kw' })),
    ],
    [inputSlugs, outputSlugs, paramSlugs, inputUnits, outputUnits, paramUnits],
  );

  // IO slug set for detecting property-mode autocomplete (after `<slug>.`).
  const ioSlugSet = useMemo(
    () => new Set([...inputSlugs, ...outputSlugs]),
    [inputSlugs, outputSlugs],
  );

  const updateAutocomplete = useCallback(
    textarea => {
      const cursor = textarea.selectionStart;
      const value = textarea.value;
      let start = cursor;
      while (start > 0 && /[A-Za-z0-9_]/.test(value[start - 1])) start--;
      let end = cursor;
      while (end < value.length && /[A-Za-z0-9_]/.test(value[end])) end++;

      const prefix = value.slice(start, cursor);
      const isPropertyMode = start > 0 && value[start - 1] === '.';

      let items = [];

      // Property-mode: `<known_slug>.<partial>` → suggest property names
      // and conversion accessors. We show the full menu even when the
      // prefix is empty (cursor right after the dot), so the user can
      // discover available properties without typing.
      if (isPropertyMode) {
        let slugEnd = start - 1;
        let slugStart = slugEnd;
        while (slugStart > 0 && /[A-Za-z0-9_]/.test(value[slugStart - 1])) slugStart--;
        const slug = value.slice(slugStart, slugEnd);
        if (ioSlugSet.has(slug)) {
          const slugProps = ioProperties?.[slug] ?? {};
          for (const name of PROPERTIES) {
            if (slugProps[name] && name.startsWith(prefix) && name !== prefix) {
              items.push({
                slug: name,
                kind: 'identifier',
                tag: 'property',
                unit: slugProps[name].unit,
              });
            }
          }
          for (const name of DERIVED_ACCESSORS) {
            if (!name.startsWith(prefix) || name === prefix) continue;
            // Prefer the per-species unit stored in slugProps (energy
            // accessors resolve to mmBtu for gas, MJ for J-declared
            // species, etc.); fall back to the static target unit.
            const unit = slugProps[name]?.unit ?? DERIVED_ACCESSOR_UNITS[name];
            items.push({
              slug: name,
              kind: 'identifier',
              tag: 'convert',
              unit,
            });
          }
        }
      } else {
        // Normal identifier candidates — these need a prefix to filter
        // against, otherwise the list is overwhelming.
        if (!prefix) {
          closeAc();
          return;
        }
        const lets = extractLetBindings(value.slice(0, start), knownSlugs).map(s => ({
          slug: s,
          kind: 'identifier',
          tag: 'let',
        }));
        items = [...baseCandidates, ...lets]
          .filter(c => c.slug.startsWith(prefix) && c.slug !== prefix)
          .slice(0, 10);
      }

      if (items.length === 0) {
        closeAc();
        return;
      }

      const lastNewline = value.lastIndexOf('\n', start - 1);
      const col = start - (lastNewline + 1);
      const lineNum = value.slice(0, start).split('\n').length - 1;
      const top = (lineNum + 1) * EDITOR_LINE_HEIGHT + EDITOR_PADDING_TOP - textarea.scrollTop;
      const left = col * EDITOR_CHAR_WIDTH + EDITOR_PADDING_LEFT - textarea.scrollLeft;

      setAc({
        open: true,
        items,
        selectedIdx: 0,
        position: { top, left },
        start,
        end,
      });
    },
    [baseCandidates, knownSlugs, ioSlugSet, ioProperties, closeAc],
  );

  const acceptItem = useCallback(
    item => {
      const newText = text.slice(0, ac.start) + item.slug + text.slice(ac.end);
      setValue('balance', newText, { shouldDirty: true, shouldTouch: true });
      closeAc();
      const newCursor = ac.start + item.slug.length;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursor, newCursor);
        }
      }, 0);
    },
    [ac.start, ac.end, text, setValue, closeAc],
  );

  const onKeyDown = e => {
    if (!ac.open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAc(a => ({ ...a, selectedIdx: Math.min(a.selectedIdx + 1, a.items.length - 1) }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAc(a => ({ ...a, selectedIdx: Math.max(a.selectedIdx - 1, 0) }));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      acceptItem(ac.items[ac.selectedIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeAc();
    }
  };

  const onScroll = e => {
    setScroll({ top: e.currentTarget.scrollTop, left: e.currentTarget.scrollLeft });
    closeAc();
  };

  const onChange = e => {
    balanceReg.onChange(e);
    updateAutocomplete(e.currentTarget);
  };

  const onBlur = e => {
    balanceReg.onBlur(e);
    // Defer so a click on a dropdown item can fire first.
    setTimeout(closeAc, 100);
  };

  const onClickOrCaretMove = e => {
    updateAutocomplete(e.currentTarget);
  };

  return (
    // A flex column so the textarea can grow into whatever height the parent
    // gives it; its 14 rows are the minimum.
    <div className={`relative border border-gray-300 rounded overflow-hidden bg-white flex flex-col ${className}`}>
      <pre
        aria-hidden
        className="absolute inset-0 overflow-hidden pointer-events-none text-gray-800"
        style={sharedStyle}
      >
        <code
          style={{
            display: 'block',
            transform: `translate(${-scroll.left}px, ${-scroll.top}px)`,
            fontFamily: 'inherit',
          }}
        >
          {rendered}
          {'\n'}
        </code>
      </pre>
      <textarea
        ref={setRefs}
        name={balanceReg.name}
        rows={14}
        spellCheck={false}
        className="relative w-full flex-1 resize-none outline-none bg-transparent"
        style={{
          ...sharedStyle,
          color: 'transparent',
          caretColor: '#111827',
          zIndex: 1,
        }}
        onChange={onChange}
        onBlur={onBlur}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
        onKeyUp={e => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
            updateAutocomplete(e.currentTarget);
          }
        }}
        onClick={onClickOrCaretMove}
      />
      {ac.open && (
        <ul
          className="absolute bg-white border border-gray-200 rounded shadow-md text-xs font-mono"
          style={{
            top: ac.position.top,
            left: ac.position.left,
            maxHeight: '180px',
            overflowY: 'auto',
            zIndex: 30,
            minWidth: '160px',
          }}
        >
          {ac.items.map((item, idx) => (
            <li
              key={`${item.slug}-${idx}`}
              className={`px-2 py-1 cursor-pointer flex justify-between items-center gap-3 ${
                idx === ac.selectedIdx ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
              onMouseDown={e => {
                e.preventDefault();
                acceptItem(item);
              }}
              onMouseEnter={() => setAc(a => ({ ...a, selectedIdx: idx }))}
            >
              <span className="flex items-center gap-2">
                <span className={tokenColorClass(item.kind)}>{item.slug}</span>
                {item.unit ? (
                  <span className="text-gray-400 text-[10px]">{item.unit}</span>
                ) : null}
              </span>
              <span className="text-gray-400 text-[10px] uppercase tracking-wide">
                {item.tag}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

BalanceEditor.propTypes = {
  className: PropTypes.string,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  inputSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  outputSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  paramSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  inputUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
  outputUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
  paramUnits: PropTypes.arrayOf(PropTypes.string).isRequired,
  ioProperties: PropTypes.object,
  annotations: PropTypes.object,
};

export default BalanceEditor;
